// src/components/banners/MatchBanner.jsx
import React from 'react';
import { BACKGROUNDS } from '../../assets/backgrounds';
import './BannerStyles.css';

const NEON_GREEN = '#39ff14';
const NEON_YELLOW = '#f5e642';
const BLACK_BOX = 'rgba(0,0,0,0.82)';

const WIDTH = 1080;
const HEIGHT = 1350;

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;
  return `https://backend-futplay.onrender.com/${path}`;
};

const formatCreatorName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].toUpperCase();
  return `${parts[0]} ${parts[parts.length - 1]}`.toUpperCase();
};

const MatchBanner = ({ championship, match, stadiumBg, backgroundIndex = 0, sponsors = [] }) => {
  // 🔥 CORREÇÃO: Usa o backgroundIndex para escolher a imagem
  let backgroundImage = BACKGROUNDS[backgroundIndex] || BACKGROUNDS[0];
  
  // Se passou stadiumBg por parâmetro, usa ele
  if (stadiumBg) {
    backgroundImage = stadiumBg;
  }

  const matchDate = match?.date ? new Date(match.date) : null;
  const formattedDate = matchDate ? matchDate.toLocaleDateString('pt-BR') : '';
  const formattedHour = matchDate
    ? matchDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : '';

  const creatorName = championship?.creator?.name ? formatCreatorName(championship.creator.name) : 'DIRETORIA';
  const creatorAvatar = championship?.creator?.avatar ? getImageUrl(championship.creator.avatar) : null;

  const isCompleted = match?.status === 'completed';
  const hasScore = isCompleted && match?.scoreA != null && match?.scoreB != null;

  return (
    <div 
      className="banner-container"
      style={{ 
        width: WIDTH, 
        height: HEIGHT,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div className="banner-overlay" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0.45), rgba(0,0,0,0.80))',
      }} />
      
      <div className="banner-content" style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '36px 40px 30px',
        height: '100%',
      }}>
        {/* Neon Border */}
        <div className="neon-border" style={{
          position: 'absolute',
          top: 14,
          left: 14,
          right: 14,
          bottom: 14,
          border: `4px solid ${NEON_GREEN}`,
          borderRadius: 12,
          boxShadow: `0 0 18px ${NEON_GREEN}`,
          pointerEvents: 'none',
        }} />

        {/* Top Row */}
        <div className="banner-top-row" style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          zIndex: 1,
        }}>
          <div className="org-box" style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: BLACK_BOX,
            border: `1.5px solid ${NEON_GREEN}`,
            borderRadius: 8,
            padding: '10px 14px',
            gap: 10,
            maxWidth: 280,
          }}>
            {creatorAvatar && (
              <img 
                src={creatorAvatar} 
                alt="Creator" 
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 26,
                  border: `2px solid ${NEON_GREEN}`,
                }}
              />
            )}
            <div>
              <div style={{ color: NEON_GREEN, fontSize: 13, fontWeight: 700, letterSpacing: 1.5 }}>
                ORGANIZAÇÃO
              </div>
              <div style={{ color: '#fff', fontSize: 16, fontWeight: 900, marginTop: 1 }}>
                {creatorName}
              </div>
            </div>
          </div>

          <div className="champ-logo-wrapper">
            {championship?.logo ? (
              <img 
                src={getImageUrl(championship.logo)} 
                alt="Championship" 
                style={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  filter: `drop-shadow(0 0 20px ${NEON_GREEN})`,
                }}
              />
            ) : (
              <div style={{
                width: 200,
                height: 200,
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: 100,
                border: `2px solid ${NEON_GREEN}`,
              }} />
            )}
          </div>

          <div className="date-box" style={{
            alignItems: 'center',
            backgroundColor: BLACK_BOX,
            border: `1.5px solid ${NEON_GREEN}`,
            borderRadius: 8,
            padding: '10px 20px',
            maxWidth: 200,
          }}>
            <div style={{ color: NEON_GREEN, fontSize: 13, fontWeight: 700, letterSpacing: 1.5 }}>
              DATA
            </div>
            <div style={{ color: '#fff', fontSize: 24, fontWeight: 900, marginTop: 2 }}>
              {formattedDate || '-- / -- / ----'}
            </div>
          </div>
        </div>

        {/* Championship Name */}
        <div className="champ-name-container" style={{
          backgroundColor: BLACK_BOX,
          borderTop: `2px solid ${NEON_GREEN}`,
          borderBottom: `2px solid ${NEON_GREEN}`,
          width: '100%',
          alignItems: 'center',
          padding: '14px 0',
          marginBottom: 10,
          zIndex: 1,
        }}>
          <div style={{
            color: NEON_GREEN,
            fontSize: 52,
            fontWeight: 900,
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: 2,
            textShadow: `0 0 14px ${NEON_GREEN}`,
          }}>
            {championship?.name?.toUpperCase() || 'CAMPEONATO'}
          </div>
        </div>

        {/* Match Area */}
        <div className="match-area" style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
          zIndex: 1,
        }}>
          {/* Team A */}
          <div className="team-side" style={{
            width: 310,
            alignItems: 'center',
            gap: 16,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div className="badge-frame" style={{
              width: 240,
              height: 240,
              borderRadius: 12,
              border: `3px solid ${NEON_GREEN}`,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              boxShadow: `0 0 16px ${NEON_GREEN}`,
            }}>
              {match?.teamA?.badge ? (
                <img 
                  src={getImageUrl(match.teamA.badge)} 
                  alt={match.teamA.name}
                  style={{ width: 210, height: 210, objectFit: 'contain' }}
                />
              ) : (
                <div style={{ width: 210, height: 210, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8 }} />
              )}
            </div>
            <div className="team-name-box" style={{
              backgroundColor: '#000',
              border: `2px solid ${NEON_GREEN}`,
              borderRadius: 6,
              padding: '10px 18px',
              width: '100%',
              alignItems: 'center',
              textAlign: 'center',
              boxShadow: `0 0 8px ${NEON_GREEN}`,
            }}>
              <div style={{
                color: '#fff',
                fontSize: 30,
                fontWeight: 900,
                textAlign: 'center',
                letterSpacing: 0.5,
              }}>
                {match?.teamA?.name?.toUpperCase() || 'MANDANTE'}
              </div>
            </div>
          </div>

          {/* VS Block */}
          <div className="vs-block" style={{
            alignItems: 'center',
            gap: 16,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1,
          }}>
            {hasScore ? (
              <div className="score-container" style={{
                alignItems: 'center',
                gap: 10,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <div style={{
                  color: NEON_YELLOW,
                  fontSize: 22,
                  fontWeight: 900,
                  letterSpacing: 3,
                  textShadow: `0 0 8px ${NEON_YELLOW}`,
                }}>
                  RESULTADO
                </div>
                <div className="score-row" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  backgroundColor: '#000',
                  border: `3px solid ${NEON_YELLOW}`,
                  borderRadius: 12,
                  padding: '12px 28px',
                  boxShadow: `0 0 16px ${NEON_YELLOW}`,
                }}>
                  <span style={{
                    color: '#fff',
                    fontSize: 90,
                    fontWeight: 900,
                    lineHeight: '96px',
                    textShadow: `0 0 10px ${NEON_YELLOW}`,
                  }}>{match.scoreA}</span>
                  <span style={{
                    color: NEON_YELLOW,
                    fontSize: 60,
                    fontWeight: 900,
                    lineHeight: '64px',
                  }}>×</span>
                  <span style={{
                    color: '#fff',
                    fontSize: 90,
                    fontWeight: 900,
                    lineHeight: '96px',
                    textShadow: `0 0 10px ${NEON_YELLOW}`,
                  }}>{match.scoreB}</span>
                </div>
                <div style={{
                  backgroundColor: NEON_YELLOW,
                  borderRadius: 6,
                  padding: '5px 20px',
                }}>
                  <span style={{
                    color: '#000',
                    fontSize: 20,
                    fontWeight: 900,
                    letterSpacing: 3,
                  }}>ENCERRADO</span>
                </div>
              </div>
            ) : (
              <>
                <div style={{
                  color: NEON_GREEN,
                  fontSize: 96,
                  fontWeight: 900,
                  textShadow: `0 0 18px ${NEON_GREEN}`,
                  lineHeight: '100px',
                }}>VS</div>
                <div className="hour-badge" style={{
                  backgroundColor: '#000',
                  border: `2px solid ${NEON_GREEN}`,
                  borderRadius: 8,
                  padding: '8px 24px',
                  boxShadow: `0 0 10px ${NEON_GREEN}`,
                }}>
                  <span style={{
                    color: NEON_GREEN,
                    fontSize: 36,
                    fontWeight: 900,
                    letterSpacing: 2,
                  }}>{formattedHour || '--:--'}</span>
                </div>
              </>
            )}
          </div>

          {/* Team B */}
          <div className="team-side" style={{
            width: 310,
            alignItems: 'center',
            gap: 16,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div className="badge-frame" style={{
              width: 240,
              height: 240,
              borderRadius: 12,
              border: `3px solid ${NEON_GREEN}`,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              boxShadow: `0 0 16px ${NEON_GREEN}`,
            }}>
              {match?.teamB?.badge ? (
                <img 
                  src={getImageUrl(match.teamB.badge)} 
                  alt={match.teamB.name}
                  style={{ width: 210, height: 210, objectFit: 'contain' }}
                />
              ) : (
                <div style={{ width: 210, height: 210, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8 }} />
              )}
            </div>
            <div className="team-name-box" style={{
              backgroundColor: '#000',
              border: `2px solid ${NEON_GREEN}`,
              borderRadius: 6,
              padding: '10px 18px',
              width: '100%',
              alignItems: 'center',
              textAlign: 'center',
              boxShadow: `0 0 8px ${NEON_GREEN}`,
            }}>
              <div style={{
                color: '#fff',
                fontSize: 30,
                fontWeight: 900,
                textAlign: 'center',
                letterSpacing: 0.5,
              }}>
                {match?.teamB?.name?.toUpperCase() || 'VISITANTE'}
              </div>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="location-row" style={{
          width: '100%',
          alignItems: 'center',
          marginTop: 14,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}>
          <div className="location-badge" style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: BLACK_BOX,
            border: '1.5px solid rgba(57,255,20,0.4)',
            borderRadius: 8,
            padding: '10px 22px',
            gap: 8,
          }}>
            <span style={{ fontSize: 22 }}>📍</span>
            <span style={{
              color: '#fff',
              fontSize: 22,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}>
              {match?.location?.toUpperCase() || 'LOCAL A DEFINIR'}
            </span>
          </div>
        </div>

        {/* Sponsors */}
        <div className="sponsors-section" style={{
          width: '100%',
          alignItems: 'center',
          marginTop: 18,
          zIndex: 1,
        }}>
          <div style={{
            width: '90%',
            height: 1.5,
            backgroundColor: NEON_GREEN,
            marginBottom: 10,
            opacity: 0.5,
          }} />
          <div style={{
            color: '#ddd',
            fontSize: 12,
            fontWeight: 'bold',
            letterSpacing: 2,
            marginBottom: 12,
          }}>PATROCÍNIOS</div>
          <div className="sponsors-row" style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 15,
            width: '100%',
          }}>
            {sponsors && sponsors.length > 0 ? (
              sponsors.map((url, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 8,
                  padding: 8,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}>
                  <img 
                    src={url} 
                    alt={`Sponsor ${idx}`}
                    style={{ width: 80, height: 40, objectFit: 'contain' }}
                  />
                </div>
              ))
            ) : (
              Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 8,
                  padding: 8,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}>
                  <div style={{ width: 80, height: 40, backgroundColor: '#e0e0e0', borderRadius: 4 }} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchBanner;